/**
 * Live IPO quotes for the site-wide ticker tape and the landing hero, from the
 * GMP board (GET /api/market/gmp, mainboard + SME) joined with the live
 * subscription feed (GET /api/market/subscription).
 */
import { formatSignedPercent } from './gmpBoard';
import { isSameCompany } from './smeIpos';

const STATUS = {
  open: { label: 'Open', tone: 'success', rank: 0 },
  closing_today: { label: 'Closes Today', tone: 'warning', rank: 0 },
  upcoming: { label: 'Upcoming', tone: 'info', rank: 1 },
  closed: { label: 'Closed', tone: 'neutral', rank: 2 },
  allotted: { label: 'Allotted', tone: 'purple', rank: 2 },
  listed: { label: 'Listed', tone: 'neutral', rank: 3 },
};

const LIVE = ['open', 'closing_today'];

// Missing GMP sorts after every quoted one
const gmpRank = (item) => (typeof item.gmpPercent === 'number' ? item.gmpPercent : -1e9);

/**
 * Open issues first, then upcoming, then closed/allotted, then listed; the
 * highest grey market premium first within each.
 *
 * @param {object} feeds
 * @param {object[]} feeds.gmpRows - GMP board rows (selectGmpBoardRows)
 * @param {object[]} feeds.subscriptionRows - selectSubscriptionRows (+ selectSmeSubscriptionRows)
 */
export const buildTickerItems = ({ gmpRows = [], subscriptionRows = [] }, limit = 16) =>
  gmpRows
    .filter((row) => STATUS[row.status])
    .map((row) => {
      const live = subscriptionRows.find((sub) => sub.hasData && typeof sub.total === 'number' && isSameCompany(sub.company, row.name));
      const symbol = subscriptionRows.find((sub) => sub.symbol && isSameCompany(sub.company, row.name))?.symbol || '';
      let link = null;
      if (symbol) link = `/ipo-subscription/${encodeURIComponent(symbol)}`;
      else if (row.slug) link = `/ipo-gmp/${row.slug}`;
      return {
        id: row.id,
        name: row.name,
        board: row.board,
        statusKey: row.status,
        status: STATUS[row.status].label,
        statusType: STATUS[row.status].tone,
        rank: STATUS[row.status].rank,
        gmpPercent: row.hasGmp && typeof row.gmpPercent === 'number' ? row.gmpPercent : null,
        gmp: row.hasGmp && typeof row.gmpPercent === 'number' ? formatSignedPercent(row.gmpPercent, 1) : null,
        subscription: live ? `${live.total.toFixed(2)}x` : null,
        price: row.price ? `₹${row.price.toLocaleString('en-IN')}` : null,
        link,
      };
    })
    .sort((a, b) => a.rank - b.rank || gmpRank(b) - gmpRank(a) || a.name.localeCompare(b.name))
    .slice(0, limit);

// Issues worth a click right now: live or upcoming, highest grey market premium first
export const pickTrending = (items, count = 4) =>
  items.filter((item) => LIVE.includes(item.statusKey) || item.statusKey === 'upcoming').slice(0, count);

// How many issues are taking bids and how many are about to
export const countLiveIssues = (gmpRows = []) => ({
  open: gmpRows.filter((row) => LIVE.includes(row.status)).length,
  upcoming: gmpRows.filter((row) => row.status === 'upcoming').length,
});
